import { getUserMedia } from './getUserMedia';

const url = window.URL || window.webkitURL;

type Err = MediaStreamError | null;
type RawStream = MediaStream | null;
type Stream = MediaStream | string | null;

export const Camera = new class {
  isModernBrowser = false;

  error: MediaStreamError | null;
  stream: Stream;
  rawStream: RawStream;

  constructor() {
    this.stream = null;
    this.isModernBrowser = 'srcObject' in document.createElement('video');
  }

  supportsGetUserMedia() {
    return !!getUserMedia;
  }

  connected() {
    return !!this.stream;
  }

  getStream() {
    return this.stream;
  }

  getError() {
    return this.error;
  }

  connect(next: (err: string | null, stream?: Stream) => void) {
    if (this.getStream()) {
      return next(null, this.stream);
    }

    this.error = null;

    const err = (error: Err) => {
      this.error = error;
      this.stream = null;

      next('Could not connect');
    };

    if (getUserMedia) {
      getUserMedia(
        { video: true },
        (stream: MediaStream) => {
          this.setRawStream(stream);
          // createObjectURL does not work in newer browsers
          this.stream = this.isModernBrowser
            ? stream
            : url.createObjectURL(stream);
          next(null, this.stream);
        },
        err,
      );
    }
  }

  private setRawStream(rawStream: RawStream) {
    // If we happen to already have a stream, make sure we stop it, so the camera light doesn't stay on
    // (This might happen if we called connect multiple times)
    this.stop();
    this.rawStream = rawStream;
  }

  stop() {
    if (this.rawStream) {
      if (this.rawStream.stop) this.rawStream.stop();
      // Chrome deprecated and then removed the stream::stop method
      // https://developers.google.com/web/updates/2015/07/mediastream-deprecations
      if (this.rawStream.getTracks) {
        const tracks = this.rawStream.getTracks();
        if (tracks && tracks[0] && tracks[0].stop) {
          tracks[0].stop();
        }
      }
    }

    this.stream = null;
    this.rawStream = null;
  }
}();
